// Formatting helpers for integer-cents money, matching the "money is
// integer cents everywhere" rule the pricing engine and catalog domain
// modules follow (see src/domain/pricing.ts's module header). This module
// only formats for display; it never rounds or recomputes a total itself.

/** 1725 -> "$17.25". Negative cents render with a leading minus, e.g.
 * -150 -> "-$1.50", for a discount modifier shown in a breakdown line. */
export function formatCents(cents: number): string {
  const negative = cents < 0;
  // Whole cents only: a fractional-cent cost (36.8) would print "$0.36.8" otherwise.
  const magnitude = Math.round(Math.abs(cents));
  const dollars = Math.trunc(magnitude / 100);
  const remainder = String(magnitude % 100).padStart(2, "0");
  return `${negative ? "-" : ""}$${dollars}.${remainder}`;
}

/** 1725 -> "17.25", the plain decimal string the payments-stub "amount"
 * field expects (src/domain/payments.ts's amount is a plain string, not
 * cents). Never negative: a payment amount is never negative in this
 * thin slice. */
export function centsToAmountString(cents: number): string {
  if (cents < 0) {
    throw new RangeError("centsToAmountString does not accept a negative amount");
  }
  const dollars = Math.trunc(cents / 100);
  const remainder = String(cents % 100).padStart(2, "0");
  return `${dollars}.${remainder}`;
}

/** Basis points to a display percentage, e.g. 725 -> "7.25%". Only ever
 * used behind the provisional-tax label on an admin/debug surface; see
 * docs/decision-proposals/provisional-pricing-config.md: "The UI labels
 * tax as provisional pending DEC-004 wherever a rate is displayed in
 * admin surfaces; customer-facing surfaces show no such label." */
export function formatBasisPointsAsPercent(basisPoints: number): string {
  return `${(basisPoints / 100).toFixed(2)}%`;
}

// ---------------------------------------------------------------------
// Amendment 2026-08-22 (second), settings input formatting: every
// settings field presents and accepts the unit a human thinks in (a
// percentage for a rate, dollars and cents for a price), never the raw
// basis-point or integer-cent storage unit. Storage and the API stay
// unchanged; the conversion, both directions, lives here at the
// presentation boundary. The exactness rule this amendment states
// ("7.25 percent converts to exactly 725... inputs that cannot convert
// exactly to the stored unit are rejected with plain language rather
// than silently rounded") is why every "parse" helper below works on
// the raw decimal STRING rather than doing `Number(x) * 100` and
// rounding: a float multiply-then-round cannot tell "the extra
// precision was float noise" apart from "the human typed a third
// decimal digit that has no basis-point/cent equivalent", so it would
// silently round the second case exactly the way this amendment
// forbids. String parsing sidesteps the ambiguity entirely: a fractional
// part longer than the target unit supports is rejected by name, never
// coerced.
// ---------------------------------------------------------------------

/** Parses a plain non-negative decimal string into an exact integer count
 * of the smallest unit `decimalPlaces` implies (2 for both cents-per-
 * dollar and basis-points-per-percentage-point, since one basis point is
 * exactly 0.01 percentage points). Throws a plain-language RangeError,
 * naming `stepLabel` as the finest step this system stores, when the
 * string is not a plain non-negative number or carries more fractional
 * precision than the target unit supports; NEVER rounds. Exported so a
 * caller other than money.ts's own two named wrappers below (a future
 * hold-amount field, per the amendment's own "applies to every current
 * and future settings surface" scope) can reuse it directly. */
export function parseExactDecimalToSmallestUnit(
  raw: string,
  decimalPlaces: number,
  fieldLabel: string,
  stepLabel: string,
): number {
  const trimmed = raw.trim();
  // A leading "." is accepted (".5" reads as 0.5): a bartender types a half-bottle par that way.
  const match = /^(\d*)(?:\.(\d+))?$/.exec(trimmed);
  if (trimmed.length === 0 || match === null || (match[1] === "" && match[2] === undefined)) {
    throw new RangeError(`${fieldLabel} must be a plain non-negative number.`);
  }
  const wholePart = match[1] || "0";
  const fractionPart = match[2] ?? "";
  if (fractionPart.length > decimalPlaces) {
    throw new RangeError(
      `${fieldLabel} can't be more precise than ${stepLabel}. "${trimmed}" has more decimal places than that.`,
    );
  }
  const paddedFraction = fractionPart.padEnd(decimalPlaces, "0");
  const combined = `${wholePart}${paddedFraction}`.replace(/^0+(?=\d)/, "");
  const value = Number(combined);
  if (!Number.isSafeInteger(value)) {
    throw new RangeError(`${fieldLabel} is too large.`);
  }
  return value;
}

/** "7.25" -> 725, "7.255" -> throws naming 0.01% as the finest step this
 * system stores (one basis point). Never negative: no percent field in
 * this system (tax rate, tip presets) is ever negative. */
export function parsePercentToBasisPoints(raw: string, fieldLabel: string): number {
  return parseExactDecimalToSmallestUnit(raw, 2, fieldLabel, "0.01%");
}

/** "17" -> 1700, "17.005" -> throws naming one cent as the finest step
 * this system stores. Originally the exact, rejecting counterpart to
 * admin.ts's own pre-existing `parseDollarsToCents` (which used to
 * silently `Math.round` excess precision away). A UI-batch gate review
 * (2026-08-22) found that old function still backing the build-charge
 * and bottle-price catalog fields this amendment did not originally
 * name, live-proven silently absorbing a third decimal digit (2.005
 * saved as 201 cents with no message); admin.ts's saveDrinkForm and
 * saveBottleForm were moved onto this exact parser and
 * `parseDollarsToCents` was deleted, so every dollars-and-cents field in
 * this system, catalog and settings alike, now shares this one
 * rejecting parser. */
export function parseDollarsToCentsExact(raw: string, fieldLabel: string): number {
  return parseExactDecimalToSmallestUnit(raw, 2, fieldLabel, "one cent");
}

// ---------------------------------------------------------------------
// UX62 (docs/build-briefs/UX62-paid-ticket-totals.md): the ticket rail's
// persistent "Paid" row sums every CAPTURED payment on a ticket, never the
// most recent one alone (the #pay-status banner's own, narrower job). See
// that brief's Section 1 for why refunds are not netted here: a refund
// lives in a separate payment_refunds row and never rewrites a captured
// payment_attempts.amount or status, and there is no order-level bulk read
// of refunded totals, only a per-payment one
// (GET .../payment-attempts/:id/refundable-lines). This function sums the
// GROSS captured amount, the same choice describeAlreadyCollectedNote
// (main.ts) already makes for its own "already collected" note.
//
// TIP CORRECTION (owner ruling, Kerry, 2026-09-28, payments-review
// finding): payOrder and collectShare both store `amount` as
// subtotal + tax + tip (src/domain/payments.ts ~1778,
// src/domain/splitPayments.ts ~1246: `formatCentsAsAmount(subtotalCents +
// taxCents + resolvedTipCents)`), but the ticket's own Total (the price
// quote) is subtotal + tax ONLY, never tip. Summing raw `amount` for the
// Paid row therefore overstates Paid by the tip on every tipped payment,
// so a tipped, fully-covered ticket would show Paid > Total, the same
// "numbers do not agree" defect this item exists to fix, just moved one
// step over. Ruling, verbatim: "Paid + Tips lines. Paid shows the drinks
// portion paid (captured amount minus that payment's tip), so it equals
// Total when the ticket is covered; a separate 'Tips $X' row directly
// below appears only when captured tips are above zero." `tip_cents` is
// already a column on `payment_attempts` and was already present, unused,
// on the client's own PaymentAttemptDto (ui/src/api.ts) and therefore in
// every getPayments response; no server or route change was needed to
// read it.
//
// UX62-F1 (docs/build-briefs/UX62-F1-refunded-line.md), owner ruling
// 2026-09-28, "Separate Refunded line": a completed refund used to sit
// invisibly inside the gross Paid figure above (Paid was never netted for
// a refund, by the ABOVE finding's own explicit choice). Rather than net
// it now, a THIRD row, Refunded, shows the sum separately: Paid keeps
// showing exactly what was charged, unchanged from how UX62 built it.
// Unlike the tip correction above, this DID need a server change: no
// existing route returned an order-level refunded total, only a
// per-payment one requiring one round trip per payment
// (GET .../payment-attempts/:id/refundable-lines, the gap the original
// UX62 brief named and declined to close for exactly this cost reason).
// The minimal fix: `refundedCents` was added to the SAME
// getPayments/listPaymentsForOrder response this module already reads,
// computed server-side per payment attempt in one extra parallel query
// (src/domain/payments.ts), so this stays a zero-extra-round-trip read.
// ---------------------------------------------------------------------

export interface CapturedPaymentsSummary {
  /** The drinks-plus-tax portion of every Captured payment's own amount
   * (amount minus that SAME payment's own tip), summed in integer cents.
   * Equals the ticket's own Total once every line is covered, by
   * construction: Total never includes tip, and this figure never does
   * either. */
  paidCents: number;
  /** Every Captured payment's own tip, summed in integer cents. A payment
   * with a null tip_cents (no tip selection, or the internal legacy-amount
   * path that never reaches the HTTP layer) contributes 0. */
  tipCents: number;
  /** UX62-F1 (docs/build-briefs/UX62-F1-refunded-line.md). Every Captured
   * payment's own COMPLETED refund total (drinks plus tax; a refund never
   * claws back tip, DEC-035, so there is no tip component to add here),
   * summed in integer cents. Owner ruling, 2026-09-28: "Separate Refunded
   * line" -- this is NOT netted against paidCents, which keeps showing
   * the gross drinks-plus-tax amount charged, exactly as UX62 built it. */
  refundedCents: number;
  count: number;
}

/** Sums every payment whose status is exactly "Captured" into the ticket's
 * own Paid (drinks + tax), Tips, and Refunded figures, plus a count, in
 * exact integer cents throughout (never a float sum of decimal strings;
 * each amount is parsed with parseDollarsToCentsExact, the same exact
 * parser every other money field in this system already uses, then added
 * and subtracted as integers). A Failed or Pending payment contributes
 * nothing to any of the four; `refundedCents` on each payment is already
 * scoped server-side to that payment's own completed (non-Failed) refunds
 * (src/domain/payments.ts's SELECT_REFUNDED_CENTS_FOR_PAYMENT_SQL), so
 * this function only sums a figure that is already correct per payment,
 * it does not itself judge refund status. */
export function summarizeCapturedPayments(
  payments: { status: string; amount: string; tipCents: number | null; refundedCents: number }[],
): CapturedPaymentsSummary {
  let paidCents = 0;
  let tipCents = 0;
  let refundedCents = 0;
  let count = 0;
  for (const payment of payments) {
    if (payment.status !== "Captured") {
      continue;
    }
    const amountCents = parseDollarsToCentsExact(payment.amount, "captured payment amount");
    const thisTipCents = payment.tipCents ?? 0;
    paidCents += amountCents - thisTipCents;
    tipCents += thisTipCents;
    refundedCents += payment.refundedCents;
    count += 1;
  }
  return { paidCents, tipCents, refundedCents, count };
}

/** 0 -> "· 0 payments", 1 -> "· 1 payment", 2 -> "· 2 payments". Plain
 * words, correct singular/plural (design/principles.md rule 5); the
 * leading middot matches the mockup's own separator between the amount
 * and the count. */
export function formatPaidCountLabel(count: number): string {
  return `· ${count} payment${count === 1 ? "" : "s"}`;
}

/** 725 -> "7.25", the plain decimal string a percent input's value uses.
 * Lossless by construction (storage is already integer basis points, so
 * this only ever inserts a decimal point, never rounds), giving every
 * percent field in this system the "display round-trips losslessly"
 * property the amendment requires. Never negative, matching every
 * percent field in this system. */
export function basisPointsToPercentString(basisPoints: number): string {
  if (basisPoints < 0) {
    throw new RangeError("basisPointsToPercentString does not accept a negative value");
  }
  const whole = Math.trunc(basisPoints / 100);
  const remainder = String(basisPoints % 100).padStart(2, "0");
  return `${whole}.${remainder}`;
}
